/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { gsap } from 'gsap'
import Draggable from 'gsap/Draggable'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { observer } from 'mobx-react-lite'
import { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { rpBreakPoints, rpMediaQueries } from '../../core/theme'
import { useRussianPokerStore } from '../../providers/RussianPokerProvider'
import { chipsRange, chipUrl } from '../../services/chipsets'
import { BoxStore } from '../../stores/RussianPoker/Box.store'
import { BgMixin } from '../BettingBox/styles'
import { ReactComponent as DownIcon } from './img/swipeDown.svg'
import { ReactComponent as UpIcon } from './img/swipeUp.svg'

gsap.registerPlugin(Draggable, ScrollTrigger)

const Container = styled.div`
  position: absolute;
  top: 0;
  right: calc(100% - 2px);
  height: 100%;
  display: flex;
  align-items: center;
  padding: 22px 0;

  animation: slideInFromRight 0.3s ease-out;

  @keyframes slideInFromRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`
const Inside = styled.div`
  ${BgMixin};
  padding: 24px 8px;
  border-radius: 6px 0 0 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 50px;
  height: 100%;
  position: relative;
  overflow: hidden;

  ${rpMediaQueries.tablet} {
    width: 70px;
  }

  ${rpMediaQueries.desktop} {
    width: 100px;
  }
`
const ChipsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`
const ChipItem = styled.li`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: transform, opacity, filter;
  cursor: pointer;
  user-select: none;
  outline: none;
  -webkit-tap-highlight-color: transparent;

  ${rpMediaQueries.tablet} {
    height: 60px;
  }

  ${rpMediaQueries.desktop} {
    height: 70px;
  }

  img {
    display: block;
    border-radius: 50%;
    pointer-events: none;
    transition: filter 0.12s;
    outline: none;
    -webkit-tap-highlight-color: transparent;
  }
  &.active img {
    filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.65));
  }
`
const DragProxy = styled.div`
  visibility: hidden;
  position: absolute;
`

const BtnContainer = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  z-index: 7;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
`
const UpBtnContainer = styled(BtnContainer)`
  top: 0;
`

const DownBtnContainer = styled(BtnContainer)`
  bottom: 0;
`

const StyledUpIcon = styled(UpIcon)`
  width: 70%;
  height: 70%;
`

const StyledDownIcon = styled(DownIcon)`
  width: 70%;
  height: 70%;
`

type RpChipPanelProps = { box: BoxStore }

// ========== КОНСТАНТЫ ==========
const TOTAL_CHIPS = 7

// Адаптивные размеры фишек для разных брейкпоинтов
const CHIP_SIZES = {
  mobile: { center: 35, side: 25, slotHeight: 40 },
  tablet: { center: 55, side: 45, slotHeight: 60 },
  desktop: { center: 65, side: 50, slotHeight: 70 },
} as const

const WHEEL_THRESHOLD = 50 // порог накопления deltaY для переключения
const DRAG_SENSITIVITY = 1 / 140 // чувствительность драга
const INERTIA_THRESHOLD = 0.3 // порог смещения для инерции

// Визуальные параметры для разных позиций фишек
const VISUAL_PARAMS = {
  center: {
    scale: 1,
    opacity: 1,
    zIndex: 3,
    pointerEvents: 'auto',
  },
  side: {
    scale: 1, // будет пересчитываться динамически
    opacity: 0.78,
    zIndex: 2,
    pointerEvents: 'auto',
  },
  hidden: {
    scale: 0.6,
    opacity: 0,
    zIndex: 1,
    pointerEvents: 'none',
  },
} as const

// Параметры анимации
const ANIMATION = {
  snap: { duration: 0.28, ease: 'power3.out' },
  wheel: { duration: 0.35, ease: 'power2.out' },
  click: { duration: 0.3, ease: 'power2.out' },
}

// ========== УТИЛИТЫ ==========
const wrapIndex = (value: number, total: number): number => {
  const wrapped = value % total
  return wrapped < 0 ? wrapped + total : wrapped
}

const shortestPath = (from: number, to: number, total: number): number => {
  let delta = (to - from) % total
  if (delta > total / 2) delta -= total
  if (delta < -total / 2) delta += total
  return delta
}

// Определяем текущий размер фишек на основе ширины окна
const getCurrentChipSizes = () => {
  const width = window.innerWidth
  if (width >= rpBreakPoints[2]) return CHIP_SIZES.desktop
  if (width >= rpBreakPoints[1]) return CHIP_SIZES.tablet
  return CHIP_SIZES.mobile
}

export const RpChipPanel: FC<RpChipPanelProps> = observer(() => {
  const { selectedChip, patchStore, game } = useRussianPokerStore()

  // ========== REFS ==========
  const listRef = useRef<HTMLUListElement>(null)
  const proxyRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const offsetRef = useRef(0) // текущая позиция (float)
  const snapTween = useRef<gsap.core.Tween | null>(null)
  const wheelAccumulator = useRef(0)
  const scrollTriggerInstance = useRef<ScrollTrigger | null>(null)
  const isDragging = useRef(false)
  const audioContextRef = useRef<AudioContext | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const isInitialized = useRef(false)
  const previousOffset = useRef<number>(0) // для отслеживания пересечения центра
  const items = useMemo(() => chipsRange.slice(0, TOTAL_CHIPS), [])

  // Получаем текущий размер фишек
  const chipSizes = getCurrentChipSizes()

  // ========== ИНИЦИАЛИЗАЦИЯ АУДИО ==========
  const initAudio = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio('/sounds/click.mp3')
      audioRef.current.volume = 0.3
      // Предзагружаем для быстрого воспроизведения
      audioRef.current.load()
    }
  }, [])

  // ========== ЗВУК ЩЕЛЧКА ==========
  const playClickSound = useCallback(() => {
    // Воспроизводим только если пользователь уже взаимодействовал
    if (!isInitialized.current || !audioRef.current) return

    try {
      // Сбрасываем на начало и воспроизводим
      audioRef.current.currentTime = 0
      audioRef.current.play().catch(() => {
        // Игнорируем ошибки autoplay policy
      })
    } catch (error) {
      // Игнорируем ошибки
    }
  }, [])

  // ========== РЕНДЕР ФИШЕК ==========
  const renderChips = useCallback(() => {
    if (!listRef.current) return

    const chipElements = Array.from(listRef.current.children) as HTMLElement[]
    const currentOffset = offsetRef.current

    // Получаем адаптивные размеры фишек
    const currentSizes = getCurrentChipSizes()
    const { side, center, slotHeight } = currentSizes
    const sideScale = side / center // пересчитываем scale для боковых

    // Динамический расчет количества видимых фишек на основе высоты контейнера
    const containerHeight = listRef.current.parentElement?.clientHeight || 0
    const maxVisibleSlots = Math.ceil(containerHeight / slotHeight)
    const visibleRange = maxVisibleSlots / 2 // половина слотов в каждую сторону

    // Проверяем пересечение центральной позиции при драге
    if (isInitialized.current && isDragging.current) {
      const previousIndex = Math.round(previousOffset.current)
      const currentIndex = Math.round(currentOffset)

      // Звук воспроизводится когда фишка пересекает центр (целое значение)
      if (previousIndex !== currentIndex) {
        playClickSound()
      }
    }

    // Сохраняем текущую позицию для следующего кадра
    previousOffset.current = currentOffset

    chipElements.forEach((element, index) => {
      const distance = shortestPath(currentOffset, index, TOTAL_CHIPS)
      const absDistance = Math.abs(distance)

      const yPosition = distance * slotHeight
      let scale: number
      let opacity: number
      let zIndex: number
      let pointerEvents: string

      // Показываем фишки в пределах видимой области
      if (absDistance < 0.5) {
        // Центральная фишка (или около центра)
        const centerBlend = 1 - absDistance * 2 // 1.0 в центре, 0.0 на границе
        scale =
          sideScale + (VISUAL_PARAMS.center.scale - sideScale) * centerBlend
        opacity =
          VISUAL_PARAMS.side.opacity +
          (VISUAL_PARAMS.center.opacity - VISUAL_PARAMS.side.opacity) *
            centerBlend
        zIndex = 3
        pointerEvents = 'auto'
      } else if (absDistance <= visibleRange) {
        // Боковые фишки в пределах видимого диапазона
        const sideBlend = Math.max(
          0,
          1 - (absDistance - 0.5) / (visibleRange - 0.5)
        )
        scale =
          VISUAL_PARAMS.hidden.scale +
          (sideScale - VISUAL_PARAMS.hidden.scale) * sideBlend
        opacity =
          VISUAL_PARAMS.hidden.opacity +
          (VISUAL_PARAMS.side.opacity - VISUAL_PARAMS.hidden.opacity) *
            sideBlend
        zIndex = 2
        pointerEvents = absDistance <= visibleRange - 0.5 ? 'auto' : 'none'
      } else {
        // Скрытые элементы за пределами видимой области
        ;({ scale, opacity, zIndex, pointerEvents } = VISUAL_PARAMS.hidden)
      }

      gsap.set(element, {
        xPercent: -50,
        yPercent: -50,
        y: yPosition,
        scale,
        opacity,
        zIndex,
        pointerEvents,
      })

      // Активная фишка (в центре)
      element.classList.toggle('active', absDistance < 0.0001)
    })
  }, [playClickSound])

  // ========== АНИМАЦИЯ К БЛИЖАЙШЕЙ ПОЗИЦИИ ==========
  const snapToPosition = useCallback(
    (targetOffset: number, animation = ANIMATION.snap, withSound = false) => {
      const endPosition =
        offsetRef.current +
        shortestPath(offsetRef.current, targetOffset, TOTAL_CHIPS)

      snapTween.current?.kill()
      snapTween.current = gsap.to(offsetRef, {
        current: endPosition,
        duration: animation.duration,
        ease: animation.ease,
        onUpdate: () => {
          // Проверяем пересечение центра во время анимации (только если звук разрешён)
          if (withSound && isInitialized.current) {
            const previousIndex = Math.round(previousOffset.current)
            const currentIndex = Math.round(offsetRef.current)

            if (previousIndex !== currentIndex) {
              playClickSound()
            }
          }
          previousOffset.current = offsetRef.current
          renderChips()
        },
        onComplete: () => {
          offsetRef.current = wrapIndex(offsetRef.current, TOTAL_CHIPS)
          renderChips()

          // Обновляем выбранную фишку в store
          const currentIndex = Math.round(
            wrapIndex(offsetRef.current, TOTAL_CHIPS)
          )
          const currentChip = items[currentIndex]

          if (currentChip !== selectedChip) {
            patchStore({ selectedChip: currentChip })
          }
        },
      })
    },
    [items, selectedChip, patchStore, renderChips, playClickSound]
  )

  // ========== НАВИГАЦИЯ ПО КНОПКАМ ==========
  const handleUpClick = useCallback(() => {
    isInitialized.current = true
    initAudio()
    const targetIndex = wrapIndex(
      Math.round(offsetRef.current) - 1,
      TOTAL_CHIPS
    )
    snapToPosition(targetIndex, ANIMATION.click, true)
  }, [snapToPosition, initAudio])

  const handleDownClick = useCallback(() => {
    isInitialized.current = true
    initAudio()
    const targetIndex = wrapIndex(
      Math.round(offsetRef.current) + 1,
      TOTAL_CHIPS
    )
    snapToPosition(targetIndex, ANIMATION.click, true)
  }, [snapToPosition, initAudio])

  useEffect(() => {
    if (!listRef.current || !proxyRef.current) return undefined

    const minBet = game.config.limit.min

    // Находим ближайшую фишку >= minBet
    const findClosestChip = () => {
      const validChips = items.filter((chip) => chip >= minBet)
      return validChips.length > 0 ? validChips[0] : items[items.length - 1]
    }

    const initialChip =
      selectedChip && items.includes(selectedChip)
        ? selectedChip
        : findClosestChip()

    const initialIndex = items.indexOf(initialChip)
    offsetRef.current = initialIndex

    // Устанавливаем начальную фишку в store, если она не выбрана
    if (!selectedChip || !items.includes(selectedChip)) {
      patchStore({ selectedChip: initialChip })
    }

    renderChips()

    let dragStartOffset = 0

    // ========== RESIZE OBSERVER для адаптивности ==========
    const handleResize = () => {
      renderChips()
    }
    window.addEventListener('resize', handleResize)

    // ========== DRAGGABLE ==========
    const [draggable] = Draggable.create(proxyRef.current, {
      type: 'y',
      trigger: listRef.current,

      onPress() {
        isInitialized.current = true // Помечаем что было пользовательское действие
        initAudio() // Инициализируем аудио при первом взаимодействии
        dragStartOffset = offsetRef.current
        snapTween.current?.kill()
      },

      onDrag(this: any) {
        if (this.startY === undefined || this.y === undefined) return

        isDragging.current = true
        const deltaY = this.startY - this.y
        offsetRef.current = wrapIndex(
          dragStartOffset + deltaY * DRAG_SENSITIVITY,
          TOTAL_CHIPS
        )
        renderChips()
      },

      onDragEnd(this: any) {
        isDragging.current = false

        // Используем tween для получения velocity
        const dragDelta = offsetRef.current - dragStartOffset
        const shouldAddInertia = Math.abs(dragDelta) > INERTIA_THRESHOLD

        let inertiaStep = 0
        if (shouldAddInertia) {
          inertiaStep = dragDelta > 0 ? 1 : -1
        }

        const targetIndex = Math.round(offsetRef.current) + inertiaStep
        snapToPosition(
          wrapIndex(targetIndex, TOTAL_CHIPS),
          ANIMATION.snap,
          false
        ) // Звук уже был при драге
      },
    })

    // ========== WHEEL / TRACKPAD с ScrollTrigger ==========
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault()

      if (isDragging.current) return

      wheelAccumulator.current += event.deltaY

      if (Math.abs(wheelAccumulator.current) >= WHEEL_THRESHOLD) {
        const steps = Math.floor(
          Math.abs(wheelAccumulator.current) / WHEEL_THRESHOLD
        )
        const direction = wheelAccumulator.current > 0 ? 1 : -1
        wheelAccumulator.current = 0

        // Инициализируем аудио только при первом wheel на компоненте
        if (!isInitialized.current) {
          isInitialized.current = true
          initAudio()
        }

        const targetIndex = wrapIndex(
          Math.round(offsetRef.current) + direction * steps,
          TOTAL_CHIPS
        )
        snapToPosition(targetIndex, ANIMATION.wheel, true) // Со звуком
      }
    }

    // ========== ScrollTrigger для touch/swipe ==========
    if (containerRef.current) {
      scrollTriggerInstance.current = ScrollTrigger.create({
        trigger: containerRef.current,
        start: 'top top',
        end: 'bottom bottom',
        onUpdate: (self) => {
          if (!isDragging.current && self.direction !== 0) {
            // ScrollTrigger отслеживает swipe/touch
            // Инициализируем аудио только если уже было прямое взаимодействие
            if (isInitialized.current) {
              const delta = self.direction // 1 или -1
              const targetIndex = wrapIndex(
                Math.round(offsetRef.current) + delta,
                TOTAL_CHIPS
              )
              snapToPosition(targetIndex, ANIMATION.wheel, true) // Со звуком
            }
          }
        },
      })
    }

    // ========== CLICK ==========
    const chipElements = Array.from(listRef.current.children) as HTMLElement[]
    const clickHandlers: Array<() => void> = []

    chipElements.forEach((element, index) => {
      const handler = () => {
        isInitialized.current = true
        initAudio() // Инициализируем аудио при первом взаимодействии
        const targetIndex = wrapIndex(index, TOTAL_CHIPS)
        snapToPosition(targetIndex, ANIMATION.click, true) // Со звуком
      }
      element.addEventListener('click', handler)
      clickHandlers.push(() => element.removeEventListener('click', handler))
    })

    listRef.current.addEventListener('wheel', handleWheel, { passive: false })

    // ========== CLEANUP ==========
    return () => {
      window.removeEventListener('resize', handleResize)
      draggable.kill()
      listRef.current?.removeEventListener('wheel', handleWheel)
      snapTween.current?.kill()
      scrollTriggerInstance.current?.kill()
      clickHandlers.forEach((fn) => fn())

      // Закрываем AudioContext
      if (audioContextRef.current) {
        audioContextRef.current.close()
        audioContextRef.current = null
      }
    }
  }, [items, selectedChip, renderChips, snapToPosition, patchStore, initAudio])

  return (
    <Container ref={containerRef}>
      <UpBtnContainer onClick={handleUpClick}>
        <StyledUpIcon />
      </UpBtnContainer>
      <Inside>
        <ChipsList ref={listRef}>
          {items.map((chip, i) => (
            <ChipItem key={chip} data-cy={`rp-chip-${i}`}>
              <img
                data-cy={`rp-chip-img-${i}`}
                src={chipUrl[i]}
                alt={chip.toString()}
                width={chipSizes.center}
                height={chipSizes.center}
                draggable={false}
                css={css`
                  display: block;
                `}
              />
            </ChipItem>
          ))}
        </ChipsList>
        <DragProxy ref={proxyRef} />
      </Inside>
      <DownBtnContainer onClick={handleDownClick}>
        <StyledDownIcon />
      </DownBtnContainer>
    </Container>
  )
})
